import { cn } from '@worldcoo/ui/lib/utils';
import { MenuIcon } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { useIsMobile } from '@worldcoo/ui/hooks/use-mobile';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { lazy, Suspense } from 'react';

const MobileMenu = lazy(() => import('./mobile-menu'));

const XL_BREAKPOINT = 1280;

export function Header() {
	const { t } = useLingui();
	const { language, changeLanguage } = useTranslation();
	const isMobile = useIsMobile(XL_BREAKPOINT);
	const languages = [
		{ code: 'es', label: 'ESP' },
		{ code: 'en', label: 'ENG' },
		{ code: 'ca', label: 'CAT' },
		{ code: 'it', label: 'IT' },
	] as const;

	return (
		<>
			{/* Background so the backdrop merges with the hero color when top: 0 */}
			<div className="bg-hero absolute -z-10 h-[100px] w-full" />
			<header className="bg-hero/50 sticky top-0 z-10 backdrop-blur-md">
				<div className="mx-auto flex max-w-[1310px] items-center py-4 font-medium">
					{/* Logo */}
					<Link to="/www" activeOptions={{ exact: true }} className="pr-6 pl-4">
						<span className="sr-only">{t`Worldcoo home`}</span>
						<WorldcooLogo className="h-[67.36px] w-[120px]" accentClassName="text-primary" />
					</Link>
					{/* Navigation - Desktop */}
					<nav className="mx-auto hidden px-2 xl:flex">
						<Link
							to="/www/round-up"
							className="hover:text-primary aria-[current=page]:text-primary truncate p-4 transition-colors"
						>
							{t`Charity Rounding Up`}
						</Link>
						<a href="https://app.worldcoo.com/" className="hover:text-primary truncate p-4 transition-colors">
							{t`Donation Certificate`}
						</a>
						<Link
							to="/www/about-us"
							className="hover:text-primary aria-[current=page]:text-primary truncate p-4 transition-colors"
						>
							{t`About Us`}
						</Link>
						<Link
							to="/www/transparency"
							className="hover:text-primary aria-[current=page]:text-primary truncate p-4 transition-colors"
						>
							{t`Transparency`}
						</Link>
					</nav>
					{/* Language Selector and Contact - Desktop */}
					<nav className="mr-6 hidden items-center xl:flex">
						<Link
							to="/www/contact"
							className="hover:text-primary aria-[current=page]:text-primary mr-10 p-4 transition-colors"
						>
							{t`Contact`}
						</Link>
						{languages.map((lang) => (
							<button
								key={lang.code}
								onClick={() => changeLanguage(lang.code)}
								className={cn(
									'hover:text-primary cursor-pointer rounded-full px-2.5 py-1 text-sm font-bold transition-colors',
									language === lang.code && 'border-foreground border',
								)}
							>
								{lang.label}
							</button>
						))}
					</nav>

					{/* Mobile Menu — only loads the drawer chunk on non-xl screens */}
					{isMobile && (
						<Suspense
							fallback={
								<button type="button" className="ml-auto">
									<MenuIcon className="mx-8 size-8" />
								</button>
							}
						>
							<MobileMenu language={language} changeLanguage={changeLanguage} />
						</Suspense>
					)}
				</div>
			</header>
		</>
	);
}
